
import type { User } from '../types';
import { requestJson, ERROR_MESSAGES } from './api';

const CURRENT_USER_SESSION_KEY = 'prompt_manager_current_user';

type UserStatus = 'active' | 'locked';

const normalizeUser = (user: User): User => ({
    ...user,
    hasGeminiApiKey: Boolean(user.hasGeminiApiKey),
    geminiKeyCount: Number.isFinite(user.geminiKeyCount) ? Number(user.geminiKeyCount) : 0
});

export const storeCurrentUser = (user: User | null): void => {
    if (!user) {
        sessionStorage.removeItem(CURRENT_USER_SESSION_KEY);
        return;
    }
    try {
        sessionStorage.setItem(CURRENT_USER_SESSION_KEY, JSON.stringify(normalizeUser(user)));
    } catch {
        // Ignore storage errors
    }
};


// --- Core Auth ---

export const register = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    try {
        await requestJson('/api/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        }, ERROR_MESSAGES.register);
        return { success: true, message: 'Registrierung erfolgreich.' };
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};

let currentPreAuthToken: string | null = null;

export const login = async (
    email: string,
    password: string
): Promise<{ success: boolean; message: string; user?: User; twoFactorRequired?: boolean; }> => {
    try {
        const data = await requestJson<{ user?: User; twoFactorRequired?: boolean; preAuthToken?: string }>(
            '/api/login',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            },
            ERROR_MESSAGES.login
        );

        if (data.twoFactorRequired) {
            currentPreAuthToken = data.preAuthToken || null;
            return {
                success: true,
                message: 'Zwei-Faktor-Authentifizierung erforderlich.',
                twoFactorRequired: true
            };
        }

        const { user } = data;
        if (user) {
            const normalizedUser = normalizeUser(user);
            storeCurrentUser(normalizedUser);
            return { success: true, user: normalizedUser, message: 'Anmeldung erfolgreich.' };
        }
        return { success: false, message: 'Anmeldung fehlgeschlagen.' };
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};

export const loginWithToken = async (
    email: string,
    token: string
): Promise<{ success: boolean; user?: User; message: string }> => {
    try {
        const data = await requestJson<{ user: User }>(
            '/api/login/2fa',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, token, preAuthToken: currentPreAuthToken })
            },
            ERROR_MESSAGES.twoFactor
        );
        currentPreAuthToken = null;
        const { user } = data;
        const normalizedUser = normalizeUser(user);
        storeCurrentUser(normalizedUser);
        return { success: true, user: normalizedUser, message: 'Anmeldung erfolgreich.' };
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};


export const logout = async (): Promise<void> => {
    try {
        await requestJson('/api/logout', { method: 'POST' });
    } catch {
        // ignore errors on logout
    }
    storeCurrentUser(null);
};

export const getCurrentUser = (): User | null => {
    try {
        const user = sessionStorage.getItem(CURRENT_USER_SESSION_KEY);
        if (!user) return null;
        const parsed = JSON.parse(user) as Partial<User>;
        if (typeof parsed.hasGeminiApiKey !== 'boolean') {
            parsed.hasGeminiApiKey = false;
        }
        if (typeof parsed.geminiKeyCount !== 'number') {
            parsed.geminiKeyCount = parsed.hasGeminiApiKey ? 1 : 0;
        }
        return normalizeUser(parsed as User);
    } catch {
        return null;
    }
};

// --- Password Management ---

export const changePassword = async (oldPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
    try {
        return await requestJson<{ success: boolean; message: string }>(
            '/api/password/change',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ oldPassword, newPassword })
            }
        );
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};

export const requestPasswordReset = async (
    email: string
): Promise<{ success: boolean; message: string; token?: string }> => {
    try {
        return await requestJson<{ success: boolean; message: string; token?: string }>(
            '/api/password/reset/request',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            }
        );
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};

export const resetPassword = async (
    email: string,
    token: string,
    newPassword: string
): Promise<{ success: boolean; message: string }> => {
    try {
        return await requestJson<{ success: boolean; message: string }>(
            '/api/password/reset/confirm',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, token, newPassword })
            }
        );
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};

export const deleteAccount = async (password: string): Promise<{ success: boolean; message: string }> => {
    try {
        return await requestJson<{ success: boolean; message: string }>(
            '/api/account/delete',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ password })
            }
        );
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};

// --- 2FA Management ---

export const getUserDetails = async (): Promise<{ twoFactorEnabled: boolean } | null> => {
    try {
        return await requestJson<{ twoFactorEnabled: boolean }>(
            '/api/2fa/status'
        );
    } catch {
        return null;
    }
};

export const generateTwoFactorSecret = async (): Promise<{ secret: string; otpauthUrl: string; } | null> => {
    try {
        return await requestJson<{ secret: string; otpauthUrl: string }>(
            '/api/2fa/generate',
            {
                method: 'POST'
            }
        );
    } catch {
        return null;
    }
};

export const enableTwoFactor = async (token: string): Promise<{ success: boolean; message: string }> => {
    try {
        return await requestJson<{ success: boolean; message: string }>(
            '/api/2fa/enable',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ token })
            }
        );
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};

export const disableTwoFactor = async (token: string): Promise<{ success: boolean; message: string }> => {
    try {
        return await requestJson<{ success: boolean; message: string }>(
            '/api/2fa/disable',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ token })
            }
        );
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};

export const saveGeminiApiKey = async (apiKey: string, label?: string): Promise<{ success: boolean; message: string }> => {
    try {
        return await requestJson<{ success: boolean; message: string }>(
            '/api/gemini/key',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ apiKey, label })
            }
        );
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};

export interface GeminiKeyEntry {
    id: number;
    label: string;
    createdAt?: string;
    lastUsedAt?: string | null;
    totalRequests?: number;
    failedRequests?: number;
}

interface GeminiKeyStatusResponse {
    hasKey: boolean;
    keyCount: number;
    keys: GeminiKeyEntry[];
}

export const removeGeminiApiKey = async (keyId?: number): Promise<{ success: boolean; message: string }> => {
    try {
        return await requestJson<{ success: boolean; message: string }>(
            typeof keyId === 'number' ? `/api/gemini/key/${keyId}` : '/api/gemini/key',
            {
                method: 'DELETE'
            }
        );
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};

export const refreshGeminiKeyStatus = async (): Promise<{ hasKey: boolean; keyCount: number; keys: GeminiKeyEntry[] }> => {
    try {
        const result = await requestJson<GeminiKeyStatusResponse>('/api/gemini/key');
        const current = getCurrentUser();
        if (current) {
            const nextUser = { ...current, hasGeminiApiKey: Boolean(result.hasKey), geminiKeyCount: Number(result.keyCount) || 0 };
            storeCurrentUser(nextUser);
        }
        return { hasKey: Boolean(result.hasKey), keyCount: Number(result.keyCount) || 0, keys: Array.isArray(result.keys) ? result.keys : [] };
    } catch {
        return { hasKey: false, keyCount: 0, keys: [] };
    }
};

export const testGeminiApiKey = async (apiKey?: string): Promise<{ success: boolean; message: string }> => {
    try {
        const payload = apiKey ? { apiKey } : {};
        return await requestJson<{ success: boolean; message: string }>(
            '/api/gemini/key/test',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            }
        );
    } catch (e) {
        return { success: false, message: (e as Error).message };
    }
};

// --- Admin Functions ---

export const getAllUsers = async (): Promise<User[]> => {
    try {
        return await requestJson<User[]>('/api/users');
    } catch {
        return [];
    }
};

export const getUserByEmail = async (email: string): Promise<User | null> => {
    try {
        return await requestJson<User>(`/api/users/email/${encodeURIComponent(email)}`);
    } catch {
        return null;
    }
};

export const setUserRole = async (email: string, role: 'user' | 'admin'): Promise<boolean> => {
    try {
        const target = await getUserByEmail(email);
        if (!target) return false;
        await requestJson(`/api/users/${target.id}/role`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ role })
        });
        return true;
    } catch {
        return false;
    }
};

export const setUserStatus = async (email: string, status: UserStatus): Promise<boolean> => {
    try {
        const target = await getUserByEmail(email);
        if (!target) return false;
        await requestJson(`/api/users/${target.id}/status`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status })
        });
        return true;
    } catch {
        return false;
    }
};

export const adminDeleteUser = async (email: string): Promise<boolean> => {
    try {
        const target = await getUserByEmail(email);
        if (!target) return false;
        await requestJson(`/api/users/${target.id}`, {
            method: 'DELETE'
        });
        return true;
    } catch {
        return false;
    }
};